import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Flame,
  CalendarCheck,
  Award,
  LogOut,
  Loader2,
  Lock,
  CheckCircle2,
  XCircle,
  FileCheck,
  Trophy,
  Gem,
  Crown,
  Fingerprint,
  Handshake,
  Compass,
  Sprout,
  Wallet,
  ShieldCheck,
  BookOpen,
  UserPlus,
  X,
  Search,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Modal, Select, Input } from '../../Components/ui';
import { API_URL } from '../../api/config';

// Mapeia a `chave` de cada princípio (ver carater-principios.js no backend)
// pro ícone que representa ele na Estante de Virtudes.
const ICONE_PRINCIPIO = {
  carater: Gem,
  soberania: Crown,
  individualidade: Fingerprint,
  alianca: Handshake,
  autogoverno: Compass,
  semear_colher: Sprout,
  mordomia: Wallet,
};

const STATUS_BADGE = {
  pendente: { label: 'Aguardando confirmação', cor: 'text-amber-600 dark:text-amber-400' },
  confirmado: { label: 'Confirmado', cor: 'text-green-600 dark:text-green-400' },
  rejeitado: { label: 'Não confirmado', cor: 'text-red-600 dark:text-red-400' },
};

// Tela exclusiva do aluno — perfil gamificado inspirado no layout de perfil de
// apps como o YouVersion (streak em destaque, medalhas, feed de atividade),
// mas com o tema visual padrão do resto do sistema (cards claros/escuros,
// azul de destaque, cantos arredondados) em vez do visual escuro do
// original. Totalmente mobile-first: coluna única, cards grandes, sem menu
// lateral — o aluno só vê essa tela (ver conversa: escopo é uma página só).
const STATUS_INFO = {
  presente: {
    label: 'Presente',
    Icon: CheckCircle2,
    cor: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30',
  },
  ausente: {
    label: 'Falta',
    Icon: XCircle,
    cor: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30',
  },
  justificado: {
    label: 'Falta justificada',
    Icon: FileCheck,
    cor: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30',
  },
};

const formatarData = (iso) => {
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
};

function MedalhaCard({ medalha }) {
  const pct =
    medalha.meta > 0 ? Math.min(100, Math.round((medalha.progresso / medalha.meta) * 100)) : 0;
  return (
    <div
      className={`rounded-2xl border p-3 flex flex-col items-center text-center gap-2 transition-all ${
        medalha.desbloqueada
          ? 'bg-gradient-to-b from-amber-50 to-white dark:from-amber-900/20 dark:to-gray-900 border-amber-200 dark:border-amber-900/40'
          : 'bg-gray-50 dark:bg-gray-800/40 border-gray-100 dark:border-gray-800'
      }`}
    >
      <div
        className={`w-14 h-14 rounded-full flex items-center justify-center ${
          medalha.desbloqueada
            ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400'
            : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
        }`}
      >
        {medalha.desbloqueada ? <Award className="w-7 h-7" /> : <Lock className="w-6 h-6" />}
      </div>
      <div>
        <p
          className={`text-xs font-semibold ${medalha.desbloqueada ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}
        >
          {medalha.titulo}
        </p>
        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{medalha.descricao}</p>
      </div>
      {!medalha.desbloqueada && (
        <div className="w-full">
          <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
            {medalha.progresso}/{medalha.meta}
          </p>
        </div>
      )}
    </div>
  );
}

function AlunoPerfil() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const res = await fetch(`${API_URL}/api/aluno/gamificacao`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Não foi possível carregar seus dados.');
      const body = await res.json();
      setDados(body?.data ?? body);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [token]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // --- Formação de Caráter (missões, diário, indicar colega) ---
  const [carater, setCarater] = useState(null);
  const [enviandoMissao, setEnviandoMissao] = useState(null);
  const [modalDiarioAberto, setModalDiarioAberto] = useState(false);
  const [formDiario, setFormDiario] = useState({ principio: 6, texto: '' }); // 6 = "Semear e Colher" (ver PRINCIPIO_PADRAO_DIARIO em aluno-carater.js — mantenha os dois em sincronia se a ordem dos princípios mudar)
  const [enviandoDiario, setEnviandoDiario] = useState(false);
  const [modalIndicarAberto, setModalIndicarAberto] = useState(false);
  const [buscaColega, setBuscaColega] = useState('');
  const [colegaSelecionado, setColegaSelecionado] = useState(null);
  const [formIndicar, setFormIndicar] = useState({ principio: '', texto: '' });
  const [enviandoIndicacao, setEnviandoIndicacao] = useState(false);
  const [colegas, setColegas] = useState([]);

  const carregarCarater = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/aluno/carater`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const body = await res.json();
        setCarater(body?.data ?? body);
      }
    } catch {
      /* seção opcional — falha aqui não impede o resto do perfil */
    }
  }, [token]);

  useEffect(() => {
    carregarCarater();
  }, [carregarCarater]);

  const marcarMissaoConcluida = async (missaoId) => {
    setEnviandoMissao(missaoId);
    try {
      const res = await fetch(`${API_URL}/api/aluno/missoes/${missaoId}/concluir`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) carregarCarater();
    } finally {
      setEnviandoMissao(null);
    }
  };

  const enviarDiario = async (e) => {
    e.preventDefault();
    setEnviandoDiario(true);
    try {
      const res = await fetch(`${API_URL}/api/aluno/diario`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(formDiario),
      });
      if (res.ok) {
        setModalDiarioAberto(false);
        setFormDiario({ principio: 5, texto: '' });
        carregarCarater();
      }
    } finally {
      setEnviandoDiario(false);
    }
  };

  const abrirIndicarColega = async () => {
    setColegaSelecionado(null);
    setBuscaColega('');
    setFormIndicar({ principio: '', texto: '' });
    setModalIndicarAberto(true);
    try {
      const res = await fetch(`${API_URL}/api/aluno/colegas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const body = await res.json();
        setColegas(body?.data ?? (Array.isArray(body) ? body : []));
      }
    } catch {
      /* lista fica vazia, formulário ainda funciona sem sugestões */
    }
  };

  const enviarIndicacao = async (e) => {
    e.preventDefault();
    if (!colegaSelecionado) return;
    setEnviandoIndicacao(true);
    try {
      const res = await fetch(`${API_URL}/api/aluno/indicar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ id_aluno_colega: colegaSelecionado.id, ...formIndicar }),
      });
      if (res.ok) setModalIndicarAberto(false);
    } finally {
      setEnviandoIndicacao(false);
    }
  };

  const nomePrincipioLocal = (id) => carater?.principios.find((p) => p.id === id)?.nome || '';
  const colegasFiltrados = colegas.filter((c) =>
    c.nome.toLowerCase().includes(buscaColega.toLowerCase()),
  );

  const sair = () => {
    logout();
    navigate('/aluno/entrar', { replace: true });
  };

  if (carregando) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#F9FAFB] dark:bg-gray-950">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <div className="max-w-md mx-auto px-4 pt-6 pb-10 space-y-5">
        {/* Cabeçalho */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xl font-bold flex-shrink-0">
              {user?.nome?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="min-w-0">
              <h1
                className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate"
                translate="no"
              >
                {user?.nome}
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Minha presença</p>
            </div>
          </div>
          <button
            onClick={sair}
            className="w-10 h-10 rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors flex-shrink-0"
            aria-label="Sair"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>

        {erro && (
          <div className="p-3 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 text-red-600 dark:text-red-400 text-sm">
            {erro}
          </div>
        )}

        {dados && (
          <>
            {/* Sequência (streak) — destaque principal, como "Perseverança" no exemplo */}
            <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-blue-700 p-5 text-white shadow-lg shadow-blue-600/20">
              <div className="flex items-center gap-2 mb-1">
                <Flame className="w-5 h-5" />
                <span className="text-sm font-medium opacity-90">Sequência de presença</span>
              </div>
              <p className="text-5xl font-bold leading-none">{dados.streak_atual}</p>
              <p className="text-xs opacity-75 mt-2">
                {dados.streak_atual === 0
                  ? 'Compareça hoje pra começar uma sequência!'
                  : `dia${dados.streak_atual === 1 ? '' : 's'} seguido${dados.streak_atual === 1 ? '' : 's'} sem faltar`}
              </p>

              {dados.semana_atual?.length > 0 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/15">
                  {dados.semana_atual.map((dia) => {
                    const ehPresente = dia.estado === 'presente';
                    const ehFalta = dia.estado === 'falta';
                    return (
                      <div key={dia.data} className="flex flex-col items-center gap-1.5">
                        <span className="text-[10px] font-medium opacity-60">
                          {dia.dia_semana_curto}
                        </span>
                        <div
                          className={`relative w-8 h-8 rounded-full flex items-center justify-center ${ehPresente ? 'bg-white/25' : 'bg-white/10'}`}
                        >
                          <Flame
                            className={`w-4 h-4 ${ehPresente ? 'text-orange-300' : 'text-white/35'}`}
                            fill={ehPresente ? 'currentColor' : 'none'}
                          />
                          {ehFalta && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-6 h-0.5 bg-red-300 rotate-45 rounded-full" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Frequência do mês */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <CalendarCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Frequência do mês
                </span>
              </div>
              <div className="flex items-end justify-between mb-2">
                <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {dados.frequencia_mes_pct}%
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {dados.dias_presentes_mes} de {dados.dias_esperados_mes} dias
                </span>
              </div>
              <div className="w-full h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 rounded-full transition-all"
                  style={{ width: `${Math.min(100, dados.frequencia_mes_pct)}%` }}
                />
              </div>
            </div>

            {/* Medalhas */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Medalhas</h2>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {dados.medalhas_desbloqueadas} de {dados.medalhas.length}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {dados.medalhas.map((m) => (
                  <MedalhaCard key={m.id} medalha={m} />
                ))}
              </div>
            </div>

            {/* Meses Perfeitos — mural de troféus, um card por mês fechado com 100% de frequência */}
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Meses Perfeitos
              </h2>
              {dados.meses_perfeitos.length === 0 ? (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-6 text-center">
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Nenhum mês perfeito ainda — continue firme!
                  </p>
                </div>
              ) : (
                <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1">
                  {dados.meses_perfeitos.map((m) => (
                    <div
                      key={m.mes}
                      className="flex-shrink-0 w-20 rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-gradient-to-b from-amber-50 to-white dark:from-amber-900/20 dark:to-gray-900 p-3 flex flex-col items-center text-center gap-1.5"
                    >
                      <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                        <Trophy className="w-5 h-5" />
                      </div>
                      <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                        {m.label}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Atividade recente */}
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Atividade recente
              </h2>
              {dados.atividade_recente.length === 0 ? (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-6 text-center">
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Nenhum registro de presença ainda.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {dados.atividade_recente.map((a, idx) => {
                    const info = STATUS_INFO[a.status] || STATUS_INFO.ausente;
                    const { Icon } = info;
                    return (
                      <div
                        key={idx}
                        className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-3 flex items-center gap-3"
                      >
                        <div
                          className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${info.cor}`}
                        >
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {info.label}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {formatarData(a.data)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {carater && (
          <>
            {/* Estante de Virtudes — um ícone por princípio, dourado quando o emblema (3+ atos confirmados) é desbloqueado */}
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Estante de Virtudes
              </h2>
              <div className="grid grid-cols-4 gap-2">
                {carater.principios.map((p) => {
                  const Icon = ICONE_PRINCIPIO[p.chave] || Gem;
                  const emblema = carater.emblemas.find((e) => e.principio_id === p.id);
                  const cor = emblema?.desbloqueada
                    ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400'
                    : p.confirmados > 0
                      ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500';
                  return (
                    <div
                      key={p.id}
                      className="rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-2.5 flex flex-col items-center text-center gap-1"
                    >
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center ${cor}`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <p className="text-[10px] font-medium text-gray-700 dark:text-gray-300 leading-tight">
                        {p.nome}
                      </p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500">
                        {p.confirmados}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Trilha de Liderança Servidora */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Trilha de Liderança Servidora
                </span>
              </div>
              <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {carater.trilha.nivel_atual}
              </p>
              {carater.trilha.proximo_nivel ? (
                <>
                  <div className="w-full h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden mt-2">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, Math.round((carater.trilha.progresso / carater.trilha.meta) * 100))}%`,
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                    Faltam {carater.trilha.meta - carater.trilha.progresso} ato
                    {carater.trilha.meta - carater.trilha.progresso === 1 ? '' : 's'} pra chegar a "
                    {carater.trilha.proximo_nivel}"
                  </p>
                </>
              ) : (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                  Nível máximo alcançado!
                </p>
              )}
            </div>

            {/* Missões de Caráter */}
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Missões de Caráter
              </h2>
              {carater.missoes_ativas.length === 0 ? (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-6 text-center">
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Nenhuma missão disponível agora.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {carater.missoes_ativas.map((m) => (
                    <div
                      key={m.id}
                      className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-3"
                    >
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {m.titulo}
                      </p>
                      {m.descricao && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {m.descricao}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-gray-400 dark:text-gray-600 uppercase tracking-wide">
                          {nomePrincipioLocal(m.principio)}
                        </span>
                        {(m.status_aluno === null || m.status_aluno === 'rejeitado') && (
                          <button
                            onClick={() => marcarMissaoConcluida(m.id)}
                            disabled={enviandoMissao === m.id}
                            className="text-xs font-semibold text-blue-600 dark:text-blue-400 disabled:opacity-50"
                          >
                            {m.status_aluno === 'rejeitado'
                              ? 'Tentar de novo'
                              : 'Marquei como feita'}
                          </button>
                        )}
                        {m.status_aluno && m.status_aluno !== null && (
                          <span
                            className={`text-xs font-medium flex items-center gap-1 ${STATUS_BADGE[m.status_aluno].cor}`}
                          >
                            {m.status_aluno === 'confirmado' && (
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            )}
                            {STATUS_BADGE[m.status_aluno].label}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Diário de Semeadura */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Diário de Semeadura
                </h2>
                <button
                  onClick={() => setModalDiarioAberto(true)}
                  className="flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-blue-400"
                >
                  <BookOpen className="w-3.5 h-3.5" /> Nova reflexão
                </button>
              </div>
              {carater.diario.length === 0 ? (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-6 text-center">
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Nenhuma reflexão ainda.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {carater.diario.map((d) => (
                    <div
                      key={d.id}
                      className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-3"
                    >
                      <p className="text-sm text-gray-700 dark:text-gray-300">{d.texto}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-gray-400 dark:text-gray-600">
                          {formatarData(d.data)}
                        </span>
                        <span className={`text-xs font-medium ${STATUS_BADGE[d.status].cor}`}>
                          {STATUS_BADGE[d.status].label}
                        </span>
                      </div>
                      {d.comentario_professor && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1.5 italic">
                          "{d.comentario_professor}"
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Indicar um colega */}
            <button
              onClick={abrirIndicarColega}
              className="w-full flex items-center justify-center gap-2 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-3 text-sm font-semibold text-blue-600 dark:text-blue-400"
            >
              <UserPlus className="w-4 h-4" /> Indicar um colega
            </button>
          </>
        )}
      </div>

      <Modal
        isOpen={modalDiarioAberto}
        onClose={() => setModalDiarioAberto(false)}
        title="Nova reflexão"
        maxWidth="md"
      >
        <form onSubmit={enviarDiario} className="space-y-4">
          <Select
            label="Princípio"
            value={formDiario.principio}
            onChange={(e) => setFormDiario((f) => ({ ...f, principio: e.target.value }))}
            options={(carater?.principios || []).map((p) => ({ value: p.id, label: p.nome }))}
          />
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              O que você viveu?
            </label>
            <textarea
              value={formDiario.texto}
              onChange={(e) => setFormDiario((f) => ({ ...f, texto: e.target.value }))}
              rows={4}
              placeholder="Conte uma situação em que você viu claramente a consequência de uma escolha..."
              className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={enviandoDiario || formDiario.texto.trim().length < 10}
            className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-all disabled:opacity-40"
          >
            {enviandoDiario ? 'Enviando...' : 'Enviar reflexão'}
          </button>
        </form>
      </Modal>

      <Modal
        isOpen={modalIndicarAberto}
        onClose={() => setModalIndicarAberto(false)}
        title="Indicar um colega"
        maxWidth="md"
      >
        <form onSubmit={enviarIndicacao} className="space-y-4">
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              Colega
            </label>
            {colegaSelecionado ? (
              <div className="flex items-center justify-between px-3 h-10 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <span className="text-sm text-gray-900 dark:text-gray-100" translate="no">
                  {colegaSelecionado.nome}
                </span>
                <button
                  type="button"
                  onClick={() => setColegaSelecionado(null)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  icon={Search}
                  placeholder="Busque pelo nome..."
                  value={buscaColega}
                  onChange={(e) => setBuscaColega(e.target.value)}
                />
                {buscaColega.trim().length > 0 && (
                  <div className="absolute z-10 mt-1 w-full max-h-40 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                    {colegasFiltrados.length === 0 ? (
                      <p className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">
                        Nenhum colega encontrado.
                      </p>
                    ) : (
                      colegasFiltrados.map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          onClick={() => {
                            setColegaSelecionado(c);
                            setBuscaColega('');
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                          translate="no"
                        >
                          {c.nome}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <Select
            label="Princípio"
            value={formIndicar.principio}
            onChange={(e) => setFormIndicar((f) => ({ ...f, principio: e.target.value }))}
            options={[
              { value: '', label: 'Selecione...' },
              ...(carater?.principios || []).map((p) => ({ value: p.id, label: p.nome })),
            ]}
          />
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              O que você viu?
            </label>
            <textarea
              value={formIndicar.texto}
              onChange={(e) => setFormIndicar((f) => ({ ...f, texto: e.target.value }))}
              rows={3}
              placeholder="Conte o que ele fez..."
              className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={
              enviandoIndicacao ||
              !colegaSelecionado ||
              !formIndicar.principio ||
              formIndicar.texto.trim().length < 10
            }
            className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-all disabled:opacity-40"
          >
            {enviandoIndicacao ? 'Enviando...' : 'Enviar indicação'}
          </button>
        </form>
      </Modal>
    </div>
  );
}

export default AlunoPerfil;
